import { createContext, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { equipmentService } from '../../../shared/services/equipments/equipmentsServices';
import { useNavigate, useParams } from 'react-router-dom';
import { roomService } from '../../../shared/services/rooms/roomsServices';

export interface Equipment {
  id: string;
  name: string;
  serialWiegan: string;
  counter: number;
  receptors: number;
  hosts: number;
  direction: string;
  portQuantity: number;
  finalPortQuantity: number;
  mac: string;
  tenantId: string;
  tenantName?: string;
  roomId: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Room {
  id: string;
  name: string;
}

interface EquipmentContextData {
  // Listagem
  equipments: Equipment[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (equipment: Equipment) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedEquipment: Equipment | null;
  openDeleteModal: (equipment: Equipment) => void;
  closeDeleteModal: () => void;
  handleDeleteEquipment: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  equipmentData: Equipment | null;
  isDataLoading: boolean;
  createEquipment: (data: Omit<Equipment, 'id'>) => Promise<void>;
  updateEquipment: (id: string, data: Partial<Equipment>) => Promise<void>;
  isSubmitting: boolean;
  getEquipmentById: (id: string) => Equipment;

  // Rooms
  rooms: Room[];
  loadingRooms: boolean;
  loadRooms: () => Promise<void>;
  setError: (error: string | null) => void;
}

interface EquipmentProviderProps {
  children: ReactNode;
}

export const EquipmentContext = createContext({} as EquipmentContextData);

export function EquipmentProvider({ children }: EquipmentProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Query para listar equipamentos
  const { data: equipments = [], isLoading } = useQuery({
    queryKey: ['equipments'],
    queryFn: async () => {
      try {
        const response = await equipmentService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar equipamentos';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar equipamentos');
        }
        return [];
      }
    }
  });

  // Query para buscar dados do equipamento no modo de edição
  const { data: equipmentData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['equipment', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await equipmentService.getById(id);
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar equipamento';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar equipamento');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Query para listar salas
  const { data: rooms = [], isLoading: loadingRooms } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      try {
        const response = await roomService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar salas';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar salas');
        }
        return [];
      }
    }
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);

  // Mutation para deletar equipamento
  const deleteMutation = useMutation({
    mutationFn: (id: string) => equipmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipments'] });
      navigate('/equipments');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/equipments/new');
  };

  const handleView = (equipment: Equipment) => {
    navigate(`/equipments/${equipment.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedEquipment(null);
  };

  const handleDeleteEquipment = () => {
    if (selectedEquipment?.id) {
      deleteMutation.mutate(selectedEquipment.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/equipments/${id}/edit`);
  };

  // Mutations para criar e atualizar equipamentos
  const createMutation = useMutation({
    mutationFn: (data: Omit<Equipment, 'id'>) => equipmentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipments'] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar equipamento';
        setError(errorMessage);
      } else {
        setError('Erro ao criar equipamento');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Equipment> }) => 
      equipmentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipments'] });
      queryClient.invalidateQueries({ queryKey: ['equipment', id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar equipamento';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar equipamento');
      }
    }
  });

  const createEquipment = async (data: Omit<Equipment, 'id'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateEquipment = async (id: string, data: Partial<Equipment>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const loadRooms = async () => {
    await queryClient.invalidateQueries({ queryKey: ['rooms'] });
  };

  const getEquipmentById = (id: string) => {
    const equipment = equipments.find(eq => eq.id === id);
    if (!equipment) {
      throw new Error('Equipamento não encontrado');
    }
    return equipment;
  };

  return (
    <EquipmentContext.Provider
      value={{
        // Listagem
        equipments,
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedEquipment,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteEquipment,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        equipmentData,
        isDataLoading,
        createEquipment,
        updateEquipment,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getEquipmentById,

        // Rooms
        rooms,
        loadingRooms,
        loadRooms,
        setError
      }}
    >
      {children}
    </EquipmentContext.Provider>
  );
} 